import { useMemo, useCallback } from "react"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import RiskSummaryChart from "./summary-chart"
import { createRiskMultiDateQuery, createRiskIntradayBatchTrendQueries, TOTAL_RISK, FACTORS_RISK, SPECIFIC_RISK } from "gatherers/multi-date/risk"
import { combineResultsTolerantly, isIntradaySimulationEligible } from "gatherers/helpers"
import { filterItemsWithData, extractDatesFromFirstSeries, extractDatesFromEachSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { ModelMetadata } from "resources/models"
import { ChartColors } from "connected-components/graph/types"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useIntradayState, useIntradayBatchTrendMode, getIntradayChartMode } from "connected-components/dashboard/hooks/use-intraday-state"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import type { SourceMetricIdsChangeHandler } from "connected-components/asset-view/hooks/use-source-displayed-metric-ids"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { GRAPH_RISK_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { useGraphData } from "../../hooks/use-graph-data"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"
import { useSourceGraphMetricIds } from "../../hooks/use-source-graph-metric-ids"

interface RiskGraphProps {
  modelMetadata: ModelMetadata
  onMetricsChange?: SourceMetricIdsChangeHandler
}

interface RiskItem {
  id: string
  label: string
  color: string
  category?: string
  disabled: boolean
  type: string
}

function createSummaryRiskItems(disabledItems: Record<string, boolean>, chartColors: ChartColors): RiskItem[] {
  return [
    {
      id: TOTAL_RISK,
      label: "Total Risk",
      color: chartColors.total,
      disabled: Boolean(disabledItems[TOTAL_RISK]),
      type: "overall",
    },
    {
      id: FACTORS_RISK,
      label: "Factor Risk",
      color: chartColors.factor,
      disabled: Boolean(disabledItems[FACTORS_RISK]),
      type: "overall",
    },
    {
      id: SPECIFIC_RISK,
      label: "Specific Risk",
      color: chartColors.alpha,
      disabled: Boolean(disabledItems[SPECIFIC_RISK]),
      type: "overall",
    },
  ]
}

function createCategoryFactorRiskItems(
  modelMetadata: ModelMetadata,
  selectedCategory: string,
  disabledItems: Record<string, boolean>,
): RiskItem[] {
  if (!modelMetadata?.factors) return []

  return modelMetadata.factors?.filter(factor => factor.category === selectedCategory)
    .map(factor => ({
      id: factor.id,
      label: factor.label,
      category: factor.category,
      disabled: Boolean(disabledItems[factor.id]),
      type: "factor",
      color: "",
    }))
}

export default function RiskGraph({ modelMetadata, onMetricsChange }: RiskGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter,
    normalization,
    instrument,
    comparisonInstrument,
    secondInstrument,
    isEndVsStartDateComparison,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
    isIntradayMode,
  } = useGraphData()

  const { selectedCategory } = useGraphTemplateChart(GRAPH_RISK_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS)
  const [{ lastIntradayBatchId }] = useIntradayState()
  const intradayBatchId = intradayBatchMetadata?.id

  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const { isBatchTrendMode, completedBatches, isTruncated } = useIntradayBatchTrendMode(startDate, endDate, isIntradayMode, lastIntradayBatchId, intradayBatchId)

  const handleDotClick = useCallback((clickPosition: ClickPosition | undefined, focusedItemId: string | null | undefined) => {
    if (!panelId || !focusedItemId) return
    // Summary chart lines ("risk-total", "risk-factors", "risk-specific") are internal IDs,
    // not model factor IDs. Only forward focusedItemId as selectedFactor in the factor view.
    const selectedFactor = selectedCategory !== "total" ? focusedItemId : undefined
    // A batch-trend point is stamped with its own batch's asOf, not the latest — resolve the
    // clicked point back to its batch so drilldown doesn't open with the wrong snapshot.
    const clickedBatch = isBatchTrendMode ? completedBatches.find(batch => batch.id === clickPosition?.batchId) : undefined
    // clickedBatch.date is authoritative; clickPosition.date can shift a day across timezones.
    const snapshotDate = clickedBatch?.date ?? clickPosition?.date ?? endDate
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.risk",
      title: "Asset View: Risk",
      view: "risk",
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate: snapshotDate,
        isIntradayMode,
        // A live-tick click has no clickedBatch; don't fall back to intradayBatchMetadata's latest batch.
        ...resolveIntradayDrilldownFields(isIntradayMode, isBatchTrendMode ? clickedBatch : intradayBatchMetadata, intradayPricingAsOf),
        filter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedFactor,
        selectedCategory: selectedCategory === "total" ? "factors" : selectedCategory,
        clickedDate: snapshotDate,
        frozenSnapshot: { endDate: snapshotDate },
      },
    }))
  }, [panelId, openDrilldown, selectedCategory, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, filter, normalization, isEndVsStartDateComparison, isBatchTrendMode, completedBatches])

  const baseWithSegment = useBaseWithSegment(base ?? null, filter, normalization)
  const segment = useFilteredSegment(filter, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors() as ChartColors
  const currentItems = useMemo(() => {
    if (selectedCategory === "total") {
      return createSummaryRiskItems(disabledItems, chartColors)
    }
    return createCategoryFactorRiskItems(modelMetadata, selectedCategory, disabledItems)
  }, [modelMetadata, selectedCategory, disabledItems, chartColors])

  const enabledFactorIds = useMemo(
    () => (selectedCategory === "total" ? null : currentItems.filter((item) => !item.disabled).map((item) => item.id)),
    [selectedCategory, currentItems],
  )
  useSourceGraphMetricIds({ itemIds: enabledFactorIds, onMetricsChange })

  const filterItems = useCallback(
    (items: RiskItem[], data: unknown[]) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const gatherers = useMemo(() => {
    const liveQuery = createRiskMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      startDate,
      endDate,
      isIntradayMode,
      { batchId: intradayBatchId, lastIntradayBatchId },
    )
    // Batches append to the live-tick series instead of replacing it.
    if (!isBatchTrendMode) return [liveQuery]
    return [liveQuery, ...createRiskIntradayBatchTrendQueries(modelMetadata.id, instrumentWithSegment, baseWithSegment, completedBatches)]
  }, [isBatchTrendMode, completedBatches, modelMetadata, instrumentWithSegment, baseWithSegment, startDate, endDate, isIntradayMode, intradayBatchId, lastIntradayBatchId])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, intradayPricingAsOf, lastIntradayBatchId)

  const Renderer = selectedCategory === "total" ? RiskSummaryChart : MultiLineGraph
  const intradayChartMode = getIntradayChartMode(isBatchTrendMode, isIntradaySimulationEligible(isIntradayMode, startDate, endDate, intradayBatchId, lastIntradayBatchId))
  const isOneDayMode = intradayChartMode !== "none"
  const noDataMessage = isOneDayMode ? "Intraday risk data isn't available yet." : undefined

  return <DataIntegrator
    dataTransform={isBatchTrendMode ? extractDatesFromEachSeries : extractDatesFromFirstSeries}
    combine={isBatchTrendMode ? combineResultsTolerantly : undefined}
    metrics={gatherers}
    Renderer={Renderer}
    rendererProps={{
      items: currentItems,
      filterItems: selectedCategory === "total" ? undefined : filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      onDotClick: handleDotClick,
      loadingMessage: "Loading risk data...",
      errorMessage: "Failed to load risk data. Please try again.",
      emptyMessage: "No risk data available.",
      noDataMessage,
      isOneDayMode,
      intradayChartMode,
      isTruncated,
    }}
  />
}
